import dayjs from 'dayjs';
import filter from 'lodash/filter';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import some from 'lodash/some';
import uniqBy from 'lodash/uniqBy';
import { DAYJS_FORMAT } from 'lib/constants';
import { isNada } from 'modules/utils/ArrayUtils';
import { MhpDetails } from 'modules/types/common';
import { capitalizeEachWord } from 'modules/utils/StringUtils';
import { objectToQsParam } from 'modules/utils/UrlUtils';
import { GetTestResultDetails } from 'store/testResults/actions';
import {
  TestResult,
  TestResultsFilter,
  SortByValues,
  DateRangeFilterParams,
  PresentedForm,
  PresentedFormFeatureType,
  TestResultContentType,
  TestResultsFilterType,
  NO_PROVIDER,
  TestResultsFilterCategoryMap,
  TestResultCategory
} from './types';

export const createSearchParamsString = (args: Record<string, string>) => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(args)) {
    searchParams.append(key, value);
  }

  return searchParams.toString();
};

export const getLastThreeYears = () => {
  const currentYear = Number(dayjs().format('YYYY'));
  return [String(currentYear), String(currentYear - 1), String(currentYear - 2)];
};

export const orderTestTypeFilterOptions = (testTypes: TestResultsFilter[] = []) => {
  const order = [
    TestResultCategory.LAB,
    TestResultCategory.MICROBIOLOGY,
    TestResultCategory.PATHOLOGY,
    TestResultCategory.IMAGING
  ];
  testTypes.forEach((testType: TestResultsFilter) => {
    const indexOfPlacement = order.findIndex(o => o === testType.value);
    if (indexOfPlacement !== -1) {
      order.splice(indexOfPlacement, 1, testType);
    }
  });
  return filter(order, o => typeof o === 'object') || [];
};

export const getFilterOptions = (testResults: TestResult[]) => {
  const testTypes = orderTestTypeFilterOptions(
    map(uniqBy(testResults, 'category'), (testResult: TestResult) => {
      return {
        type: TestResultsFilterType.TEST_TYPE,
        label: TestResultsFilterCategoryMap?.[testResult?.category],
        value: testResult?.category,
        checked: false
      };
    })
  );

  const providers = map(
    filter(
      uniqBy(testResults, result => result.providerNames?.join(' ')),
      (testResult: TestResult) => !isNada(testResult.providerNames)
    ),
    (testResult: TestResult) => {
      const display = testResult?.providerNames?.join(' ');
      return {
        type: TestResultsFilterType.PROVIDER,
        label: capitalizeEachWord(display),
        value: display,
        checked: false
      };
    }
  );
  // Added (No Provider) as an option if any test results with no provider
  if (some(testResults, result => isNada(result.providerNames))) {
    providers.push({
      type: TestResultsFilterType.PROVIDER,
      label: NO_PROVIDER,
      value: NO_PROVIDER,
      checked: false
    });
  }

  return [...testTypes, ...providers];
};

export const inferOrder = (sort: string): string => {
  // Infer AtoZ as the same as ASC in lodash orderBy method
  if (sort === SortByValues.asc || sort === SortByValues.atoz) return SortByValues.asc;
  // Infer ZtoA as the same as Desc in lodash orderBy method by default
  return SortByValues.desc;
};

type InferIterReturn = ((t: TestResult) => string) | ((t: TestResult) => number);

export const inferIteratees = (sort: string): InferIterReturn => {
  // Infer that AtoZ and ZtoA will sort by test result name
  if (sort === SortByValues.atoz || sort === SortByValues.ztoa) {
    return (t: TestResult) => t.name;
  }
  // Infer that ASC and DESC will sort by testDate by default
  return (t: TestResult) => +dayjs(t.testDate).unix();
};

export const filterTestResults = (
  testResults: TestResult[],
  filters: TestResultsFilter[],
  dateRangeFilters: DateRangeFilterParams,
  sortBy: SortByValues
): TestResult[] => {
  let filteredTestResults: TestResult[] = testResults;
  if (filters?.length > 0) {
    // filter by category
    if (some(filters, ['type', TestResultsFilterType.TEST_TYPE])) {
      filteredTestResults = filter(filteredTestResults, (result: TestResult) =>
        some(filters, ['value', result?.category])
      );
    }
    // filter by provider
    if (some(filters, ['type', TestResultsFilterType.PROVIDER])) {
      filteredTestResults = filter(
        filteredTestResults,
        (result: TestResult) =>
          some(filters, ['value', result?.providerNames?.join(' ')]) ||
          // If (No Provider) is selected, show test results with no provider
          (isNada(result?.providerNames) && some(filters, ['value', NO_PROVIDER]))
      );
    }
  }
  // filter by date range if year or range is selected
  if (dateRangeFilters.year) {
    const isCustom = dateRangeFilters.year === 'Custom';
    const startDate = isCustom
      ? dayjs(dateRangeFilters.startDate)
          .startOf('day')
          .format(DAYJS_FORMAT)
      : dayjs(dateRangeFilters.year)
          .startOf('year')
          .format(DAYJS_FORMAT);
    const endDate = isCustom
      ? dayjs(dateRangeFilters.endDate)
          .endOf('day')
          .format(DAYJS_FORMAT)
      : dayjs(dateRangeFilters.year)
          .endOf('year')
          .format(DAYJS_FORMAT);
    filteredTestResults = filter(filteredTestResults, (result: TestResult) => {
      const testDate = dayjs(result.testDate).format(DAYJS_FORMAT);
      return testDate >= startDate && testDate <= endDate;
    });
  }
  // return results ordered in desc by default
  return uniqBy(
    orderBy(filteredTestResults, inferIteratees(sortBy), inferOrder(sortBy)),
    'eventId'
  );
};

const findPDF = (form: PresentedForm) => form.contentType === TestResultContentType.PDF;
const findHTML = (form: PresentedForm) => form.contentType === TestResultContentType.HTML;
const findJSON = (form: PresentedForm) => form.contentType === TestResultContentType.JSON;

/**
 * Used for displaying document -- chooses text/html format over application/pdf - only selects details forms
 * @param {array} presentedForm - array of PresentedForm objects
 * @param {boolean} preferPdf - indicates preferred format for web app
 */
export const selectForm = (presentedForm: PresentedForm[], preferPdf?: boolean) => {
  const detailsForm = presentedForm.filter(
    form => form.featureType === PresentedFormFeatureType.DETAIL
  );

  const jsonForm = detailsForm.find(findJSON);
  const htmlForm = detailsForm.find(findHTML);
  const pdfForm = detailsForm.find(findPDF);

  if (preferPdf && pdfForm) {
    return pdfForm;
  }

  if (jsonForm) {
    return jsonForm;
  }

  if (htmlForm) {
    return htmlForm;
  }

  if (pdfForm) {
    return pdfForm;
  }

  return undefined;
};

export const getImageByCategory = (category: TestResultCategory | string | undefined) => {
  const { PATHOLOGY, IMAGING, MICROBIOLOGY, LAB } = TestResultCategory;
  if (category === IMAGING) return 'TestResultsImaging';
  if (category === LAB) return 'TestResultsLab';
  if (category === MICROBIOLOGY) return 'TestResultsMicrobiology';
  if (category === PATHOLOGY) return 'TestResultsPathology';
  return '';
};

export const mockTestResult = (opt?: TestResult) => ({
  isRead: true,
  category: 'Lab',
  source: 'MILLENNIUM',
  name: '',
  testDate: '',
  eventId: 17385132933,
  presentedForm: [
    {
      featureType: 'detail',
      contentType: 'application/json',
      url: ''
    },
    {
      featureType: 'download',
      contentType: 'application/pdf',
      url: ''
    },
    {
      featureType: 'share',
      contentType: 'application/json',
      url: ''
    }
  ],
  providerNames: ['SMITH, JOHN M'],
  referringProvider: null,
  providerLocation: {
    locationName: 'Warrenberg Memorial Hospital',
    locationId: '123455765',
    locationNcid: '38295723'
  },
  encounterId: 123456789,
  componentEventIds: [],
  collectionDate: '2022-01-14T19:07:00.000Z',
  ...opt
});

export const mockLabHistoricalChartData = [
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-09-24T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 6.2,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-08-24T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 5.2,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-07-24T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 6.5,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-06-24T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 5.8,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-05-24T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 9.8,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-05-08T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 8.7,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-04-24T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 4.8,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M13810065328',
    eventCd: null,
    name: 'Protein Serum',
    issued: '2020-02-24T23:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 7.2,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: null,
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  },
  {
    eventId: 'M11097927885',
    eventCd: null,
    name: 'Protein Total',
    issued: '2019-12-17T17:50:00.000Z',
    status: 'final',
    valueString: null,
    valueQuantity: {
      value: 6,
      unit: 'g/dL',
      system: 'http://unitsofmeasure.org'
    },
    interpretation: 'LOW',
    referenceRange: {
      low: {
        value: 6,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      high: {
        value: 8.4,
        unit: 'g/dL',
        system: 'http://unitsofmeasure.org'
      },
      text: '6.0 g/dL-8.4 g/dL'
    },
    notes: null,
    loincCode: '2885-2'
  }
];

const getContentType = (
  contentType: string | string[] | undefined,
  preferredType = 'text/html',
  defaultType = 'application/pdf'
) => {
  let retType = defaultType;

  if (!!contentType) {
    if (!Array.isArray(contentType)) {
      contentType = [contentType];
    }

    if ((contentType as string[]).includes(preferredType)) {
      retType = preferredType;
    }
  }

  return retType;
};

export const formatMhpDetails = (raw: MhpDetails): GetTestResultDetails => {
  const formatted = {
    ...raw
  };
  if (raw.contentType) {
    formatted.contentType = getContentType(raw.contentType);
  }
  return formatted;
};

export const getDetailsPath = (rec: TestResult) => {
  const url = `/u/health-record/test-results/${rec.eventId}`;
  return `${url}/${objectToQsParam(rec.mhpDetails)}`;
};
